import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { BarChart3, ChevronLeft, ChevronRight, RefreshCw, Search } from 'lucide-react'
import { api, getApiErrorMessage } from '../lib/api'

const CHART_COLORS = ['#0f766e', '#2563eb', '#d97706', '#7c3aed', '#be123c', '#0891b2']
const MEMBER_PAGE_SIZE = 10

export default function OrgDashboardPage() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [orgBilling, setOrgBilling] = useState(null)
  const [orgSeats, setOrgSeats] = useState(null)
  const [orgUsageStats, setOrgUsageStats] = useState(null)
  const [selectedOrgScopeId, setSelectedOrgScopeId] = useState('ALL')
  const [memberSearchTerm, setMemberSearchTerm] = useState('')
  const [memberPage, setMemberPage] = useState(1)

  const load = useCallback(async (cancelledRef = { current: false }) => {
    setLoading(true)
    setError('')
    try {
      const config = { __silent: true, __suppressErrorPageRedirect: true }
      const billingRes = await api.get('/billing/org/me', config)
      if (cancelledRef.current) return
      const billing = billingRes?.data?.data ?? null
      setOrgBilling(billing)
      if (!isActiveOrgPaysContractValue(billing)) {
        setOrgSeats(null)
        setOrgUsageStats(null)
        return
      }
      const [seatsRes, statsRes] = await Promise.all([
        api.get('/billing/org/me/seats', config),
        api.get('/billing/org/me/usage-stats', config),
      ])
      if (cancelledRef.current) return
      setOrgSeats(seatsRes?.data?.data ?? null)
      setOrgUsageStats(statsRes?.data?.data ?? null)
    } catch (e) {
      if (!cancelledRef.current) {
        setError(getApiErrorMessage(e, '조직 대시보드를 불러오지 못했습니다.'))
      }
    } finally {
      if (!cancelledRef.current) setLoading(false)
    }
  }, [])

  useEffect(() => {
    const cancelledRef = { current: false }
    load(cancelledRef)
    return () => { cancelledRef.current = true }
  }, [load])

  const contract = orgSeats?.contract || orgBilling?.contract || orgBilling || null
  const isActiveOrgPaysContract = isActiveOrgPaysContractValue(contract)
  const orgScopeOptions = useMemo(
    () => Array.isArray(orgSeats?.orgOptions) ? orgSeats.orgOptions : [],
    [orgSeats],
  )
  const orgMemberRows = useMemo(
    () => Array.isArray(orgSeats?.members) ? orgSeats.members : [],
    [orgSeats],
  )
  const scopedOrgMembers = selectedOrgScopeId === 'ALL'
    ? orgMemberRows
    : orgMemberRows.filter((member) => String(member.orgId) === String(selectedOrgScopeId))
  const memberSearchQuery = memberSearchTerm.trim().toLowerCase()
  const visibleOrgMembers = memberSearchQuery
    ? scopedOrgMembers.filter((member) => String(member?.name || '').toLowerCase().includes(memberSearchQuery))
    : scopedOrgMembers
  const memberTotalPages = Math.max(1, Math.ceil(visibleOrgMembers.length / MEMBER_PAGE_SIZE))
  const currentMemberPage = Math.min(memberPage, memberTotalPages)
  const memberPageStartIndex = (currentMemberPage - 1) * MEMBER_PAGE_SIZE
  const memberPageEndIndex = Math.min(memberPageStartIndex + MEMBER_PAGE_SIZE, visibleOrgMembers.length)
  const pagedOrgMembers = visibleOrgMembers.slice(memberPageStartIndex, memberPageEndIndex)
  const selectedOrgOption = selectedOrgScopeId === 'ALL'
    ? null
    : orgScopeOptions.find((option) => String(option.orgId) === String(selectedOrgScopeId))
  const selectedApprovedMemberCount = selectedOrgScopeId === 'ALL'
    ? (orgSeats?.scopeApprovedMemberCount ?? orgSeats?.approvedMemberCount ?? orgMemberRows.length)
    : (selectedOrgOption?.approvedMemberCount ?? scopedOrgMembers.length)
  const memberGroupRows = useMemo(
    () => Array.isArray(orgSeats?.memberGroups) ? orgSeats.memberGroups : [],
    [orgSeats],
  )
  const usageCategories = useMemo(() => collectUsageCategories(orgMemberRows), [orgMemberRows])

  useEffect(() => {
    if (selectedOrgScopeId === 'ALL') return
    const selectedExists = orgScopeOptions.some((option) => String(option.orgId) === String(selectedOrgScopeId))
    if (!selectedExists) setSelectedOrgScopeId('ALL')
  }, [orgScopeOptions, selectedOrgScopeId])

  useEffect(() => {
    setMemberPage(1)
  }, [memberSearchTerm, selectedOrgScopeId])

  useEffect(() => {
    if (memberPage > memberTotalPages) setMemberPage(memberTotalPages)
  }, [memberPage, memberTotalPages])

  return (
    <section>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">조직 대시보드</h1>
        </div>
        <button
          type="button"
          onClick={() => load()}
          disabled={loading}
          className="inline-flex min-h-[2.5rem] items-center justify-center gap-2 rounded border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
          새로고침
        </button>
      </div>

      {loading && <div className="mt-8 text-slate-500">로딩 중...</div>}
      {!loading && error && (
        <div className="mt-8 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}
      {!loading && !error && !isActiveOrgPaysContract && (
        <div className="mt-8 rounded border border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-700">
          현재 유효한 조직 계약이 없습니다.
        </div>
      )}

      {!loading && !error && isActiveOrgPaysContract && (
        <div className="mt-6 space-y-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <SummaryCard label="계약 조직" value={contract?.orgName || '-'} />
            <SummaryCard label="승인 조직원" value={`${formatNumber(orgSeats?.scopeApprovedMemberCount ?? orgSeats?.approvedMemberCount ?? 0)}명`} />
            <SummaryCard label="과금 기준 인원" value={`${formatNumber(orgSeats?.billableSeatCount ?? contract?.contractedSeats ?? 0)}명`} />
            <SummaryCard label="조회 조직원" value={`${formatNumber(selectedApprovedMemberCount ?? 0)}명`} />
          </div>

          <UsageStatsSection stats={orgUsageStats} orgOptions={orgScopeOptions} />

          {memberGroupRows.length > 0 && (
            <div className="rounded border bg-white p-5">
              <h2 className="text-lg font-semibold">조직별 현황</h2>
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                {memberGroupRows.map((group, index) => {
                  const totals = groupUsageTotals(group.members)
                  return (
                    <div key={`${group.orgId || 'org'}-${index}`} className="rounded border p-4">
                      <div className="font-semibold">{group.orgName || '조직'}</div>
                      <div className="mt-3 grid grid-cols-3 gap-3 text-sm">
                        <MiniStat label="조직원" value={`${formatNumber(group.approvedMemberCount ?? 0)}명`} />
                        <MiniStat label="사용" value={formatNumber(totals.used)} />
                        <MiniStat label="잔여" value={totals.remaining == null ? '-' : formatNumber(totals.remaining)} />
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          <div className="rounded border bg-white p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-lg font-semibold">조직원 이용 현황</h2>
              <div className="flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-2 text-sm font-medium text-slate-600" htmlFor="org-dashboard-member-search">
                  이름 검색
                  <span className="relative">
                    <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
                    <input
                      id="org-dashboard-member-search"
                      className="min-h-[2.25rem] w-44 rounded border border-slate-300 bg-white pl-8 pr-3 text-sm"
                      value={memberSearchTerm}
                      onChange={(e) => setMemberSearchTerm(e.target.value)}
                    />
                  </span>
                </label>
                {orgScopeOptions.length > 1 && (
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-slate-600" htmlFor="org-dashboard-scope">
                      조회 조직
                    </label>
                    <select
                      id="org-dashboard-scope"
                      className="min-h-[2.25rem] rounded border border-slate-300 bg-white px-3 text-sm"
                      value={selectedOrgScopeId}
                      onChange={(e) => setSelectedOrgScopeId(e.target.value)}
                    >
                      <option value="ALL">
                        전체 조직 ({formatNumber(orgSeats?.scopeApprovedMemberCount ?? orgSeats?.approvedMemberCount ?? orgMemberRows.length)}명)
                      </option>
                      {orgScopeOptions.map((option) => (
                        <option key={String(option.orgId)} value={String(option.orgId)}>
                          {option.orgName || '조직'} ({formatNumber(option.approvedMemberCount ?? 0)}명)
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-[48rem] text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="w-44 px-4 py-3 text-left">조직</th>
                    <th className="w-40 px-4 py-3 text-left">이름</th>
                    {usageCategories.length > 0 ? usageCategories.map((category) => (
                      <th key={category.key} className="w-40 px-4 py-3 text-left">{category.label}</th>
                    )) : (
                      <th className="w-40 px-4 py-3 text-left">사용/잔여</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {pagedOrgMembers.map((member, index) => (
                    <tr key={`${member.orgId || 'org'}-${member.email || member.name || index}`} className="border-t align-top">
                      <td className="px-4 py-3 font-medium">{member.orgName || '-'}</td>
                      <td className="px-4 py-3">{member.name || '-'}</td>
                      {usageCategories.length > 0 ? usageCategories.map((category) => (
                        <td key={category.key} className="px-4 py-3">
                          {formatUsageCell(findUsageItem(member.usageSummary, category))}
                        </td>
                      )) : (
                        <td className="px-4 py-3">{formatMemberUsageTotal(member)}</td>
                      )}
                    </tr>
                  ))}
                  {visibleOrgMembers.length === 0 && (
                    <tr>
                      <td className="px-4 py-8 text-center text-slate-500" colSpan={2 + Math.max(usageCategories.length, 1)}>
                        {memberSearchQuery ? '검색된 조직원이 없습니다.' : '표시할 조직원 이용 현황이 없습니다.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {visibleOrgMembers.length > 0 && (
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm">
                <div className="text-slate-500">
                  {formatNumber(memberPageStartIndex + 1)}-{formatNumber(memberPageEndIndex)} / {formatNumber(visibleOrgMembers.length)}명
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    title="이전 페이지"
                    aria-label="이전 페이지"
                    disabled={currentMemberPage <= 1}
                    onClick={() => setMemberPage((page) => Math.max(1, page - 1))}
                    className="inline-flex h-9 w-9 items-center justify-center rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                  </button>
                  <span className="min-w-[4.5rem] text-center font-medium text-slate-700">
                    {formatNumber(currentMemberPage)} / {formatNumber(memberTotalPages)}
                  </span>
                  <button
                    type="button"
                    title="다음 페이지"
                    aria-label="다음 페이지"
                    disabled={currentMemberPage >= memberTotalPages}
                    onClick={() => setMemberPage((page) => Math.min(memberTotalPages, page + 1))}
                    className="inline-flex h-9 w-9 items-center justify-center rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <ChevronRight className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  )
}

function UsageStatsSection({ stats, orgOptions = [] }) {
  const windows = useMemo(() => Array.isArray(stats?.windows) ? stats.windows : [], [stats])
  const preferredWindowKey = windows.some((window) => String(window.key || '') === 'thisMonth')
    ? 'thisMonth'
    : String(windows[0]?.key || windows[0]?.label || '')
  const [selectedWindowKey, setSelectedWindowKey] = useState(preferredWindowKey)
  const [chartMode, setChartMode] = useState('categoryByOrg')
  const [selectedFeatureKey, setSelectedFeatureKey] = useState('')
  const [selectedStatsOrgId, setSelectedStatsOrgId] = useState('')
  const selectedWindow = windows.find((window) => String(window.key || window.label || '') === selectedWindowKey) || windows[0] || null
  const featureOptions = useMemo(() => collectStatsFeatures(selectedWindow), [selectedWindow])
  const statsOrgOptions = useMemo(() => collectStatsOrgs(selectedWindow, orgOptions), [selectedWindow, orgOptions])
  const chartRows = chartMode === 'categoryByOrg'
    ? buildCategoryByOrgRows(selectedWindow, selectedFeatureKey)
    : buildOrgByCategoryRows(selectedWindow, selectedStatsOrgId)
  const selectedFeature = featureOptions.find((feature) => feature.key === selectedFeatureKey) || featureOptions[0] || null
  const selectedStatsOrg = statsOrgOptions.find((org) => String(org.orgId) === String(selectedStatsOrgId)) || statsOrgOptions[0] || null
  const chartTitle = chartMode === 'categoryByOrg'
    ? `${selectedFeature?.label || '카테고리'} 조직별 이용`
    : `${selectedStatsOrg?.orgName || '조직'} 카테고리별 이용`

  useEffect(() => {
    if (windows.length === 0) return
    const selectedExists = windows.some((window) => String(window.key || window.label || '') === selectedWindowKey)
    if (!selectedExists) setSelectedWindowKey(preferredWindowKey)
  }, [preferredWindowKey, selectedWindowKey, windows])

  useEffect(() => {
    if (featureOptions.length === 0) {
      if (selectedFeatureKey) setSelectedFeatureKey('')
      return
    }
    if (!featureOptions.some((feature) => feature.key === selectedFeatureKey)) {
      setSelectedFeatureKey(featureOptions[0].key)
    }
  }, [featureOptions, selectedFeatureKey])

  useEffect(() => {
    if (statsOrgOptions.length === 0) {
      if (selectedStatsOrgId) setSelectedStatsOrgId('')
      return
    }
    if (!statsOrgOptions.some((org) => String(org.orgId) === String(selectedStatsOrgId))) {
      setSelectedStatsOrgId(String(statsOrgOptions[0].orgId))
    }
  }, [selectedStatsOrgId, statsOrgOptions])

  if (windows.length === 0) return null

  return (
    <div className="rounded border bg-white p-5">
      <h2 className="text-lg font-semibold">이용 통계</h2>
      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
        {windows.map((window) => (
          <SummaryCard
            key={window.key || window.label}
            label={window.label || '기간'}
            value={`${formatNumber(window.totalEventCount ?? 0)}건`}
            subValue={`${formatNumber(window.activeUserCount ?? 0)}명 · 사용량 ${formatNumber(window.totalUsedUnits ?? 0)}`}
          />
        ))}
      </div>
      <div className="mt-5 border-t pt-5">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex overflow-hidden rounded border border-slate-300">
              {windows.map((window) => {
                const windowKey = String(window.key || window.label || '')
                const active = windowKey === selectedWindowKey
                return (
                  <button
                    key={windowKey}
                    type="button"
                    onClick={() => setSelectedWindowKey(windowKey)}
                    className={`min-h-[2.25rem] px-3 text-sm font-semibold ${active ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
                  >
                    {window.label || '기간'}
                  </button>
                )
              })}
            </div>
            <div className="inline-flex overflow-hidden rounded border border-slate-300">
              <button
                type="button"
                onClick={() => setChartMode('categoryByOrg')}
                className={`min-h-[2.25rem] px-3 text-sm font-semibold ${chartMode === 'categoryByOrg' ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
              >
                카테고리별 조직 통계
              </button>
              <button
                type="button"
                onClick={() => setChartMode('orgByCategory')}
                className={`min-h-[2.25rem] px-3 text-sm font-semibold ${chartMode === 'orgByCategory' ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
              >
                조직별 카테고리 통계
              </button>
            </div>
          </div>
          {chartMode === 'categoryByOrg' ? (
            <label className="flex items-center gap-2 text-sm font-medium text-slate-600">
              카테고리
              <select
                className="min-h-[2.25rem] rounded border border-slate-300 bg-white px-3 text-sm"
                value={selectedFeatureKey}
                onChange={(e) => setSelectedFeatureKey(e.target.value)}
              >
                {featureOptions.map((feature) => (
                  <option key={feature.key} value={feature.key}>{feature.label}</option>
                ))}
              </select>
            </label>
          ) : (
            <label className="flex items-center gap-2 text-sm font-medium text-slate-600">
              조직
              <select
                className="min-h-[2.25rem] rounded border border-slate-300 bg-white px-3 text-sm"
                value={selectedStatsOrgId}
                onChange={(e) => setSelectedStatsOrgId(e.target.value)}
              >
                {statsOrgOptions.map((org) => (
                  <option key={String(org.orgId)} value={String(org.orgId)}>{org.orgName || '조직'}</option>
                ))}
              </select>
            </label>
          )}
        </div>
        <StatsBarChart title={chartTitle} rows={chartRows} />
      </div>
      <div className="mt-5 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-4 py-3 text-left">기간</th>
              <th className="px-4 py-3 text-left">카테고리</th>
              <th className="px-4 py-3 text-right">이용 건수</th>
              <th className="px-4 py-3 text-right">사용량</th>
              <th className="px-4 py-3 text-right">이용자</th>
            </tr>
          </thead>
          <tbody>
            {windows.flatMap((window) => {
              const rows = Array.isArray(window.featureStats) ? window.featureStats : []
              if (rows.length === 0) {
                return [(
                  <tr key={`${window.key || window.label}-empty`} className="border-t">
                    <td className="px-4 py-3 font-medium">{window.label || '-'}</td>
                    <td className="px-4 py-3 text-slate-500" colSpan={4}>집계된 이용 통계가 없습니다.</td>
                  </tr>
                )]
              }
              return rows.map((row, index) => (
                <tr key={`${window.key || window.label}-${row.featureCode || row.featureName || index}`} className="border-t">
                  <td className="px-4 py-3 font-medium">{index === 0 ? (window.label || '-') : ''}</td>
                  <td className="px-4 py-3">{row.featureName || '기타'}</td>
                  <td className="px-4 py-3 text-right">{formatNumber(row.eventCount ?? 0)}건</td>
                  <td className="px-4 py-3 text-right">{formatFeatureUsage(row)}</td>
                  <td className="px-4 py-3 text-right">{formatNumber(row.activeUserCount ?? 0)}명</td>
                </tr>
              ))
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function StatsBarChart({ title, rows }) {
  return (
    <div className="mt-4">
      <div className="mb-3 flex min-w-0 items-center gap-2">
        <BarChart3 className="h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" />
        <h3 className="min-w-0 truncate text-sm font-bold text-slate-900">{title}</h3>
      </div>
      {rows.length === 0 ? (
        <div className="flex h-64 items-center justify-center rounded border border-dashed border-slate-200 text-sm text-slate-500">
          집계된 이용 통계가 없습니다.
        </div>
      ) : (
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} layout="vertical" margin={{ top: 8, right: 16, bottom: 8, left: 104 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
              <XAxis type="number" allowDecimals={false} tickFormatter={formatCompactNumber} tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <YAxis
                type="category"
                dataKey="name"
                width={104}
                tickFormatter={truncateChartLabel}
                tick={{ fontSize: 11 }}
                stroke="#64748b"
              />
              <Tooltip content={<StatsChartTooltip />} />
              <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                {rows.map((row, index) => (
                  <Cell key={`${row.name}-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}

function StatsChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null
  const row = payload[0]?.payload || {}
  return (
    <div className="rounded border border-slate-200 bg-white px-3 py-2 text-xs shadow-lg">
      <div className="font-semibold text-slate-900">{label || row.name || '-'}</div>
      <div className="mt-1 text-slate-600">이용 건수 {formatNumber(row.value ?? 0)}건</div>
      <div className="text-slate-600">사용량 {formatFeatureUsage(row)}</div>
      <div className="text-slate-600">이용자 {formatNumber(row.activeUserCount ?? 0)}명</div>
    </div>
  )
}

function collectStatsFeatures(window) {
  const map = new Map()
  const rows = [
    ...(Array.isArray(window?.featureStats) ? window.featureStats : []),
    ...(Array.isArray(window?.orgFeatureStats) ? window.orgFeatureStats : []),
  ]
  rows.forEach((row) => {
    const key = statsFeatureKey(row)
    if (!key || map.has(key)) return
    map.set(key, {
      key,
      label: row.featureName || '기타',
      quotaUnit: row.quotaUnit,
    })
  })
  return Array.from(map.values())
}

function collectStatsOrgs(window, orgOptions) {
  const map = new Map()
  ;(Array.isArray(orgOptions) ? orgOptions : []).forEach((org) => {
    if (org?.orgId == null) return
    map.set(String(org.orgId), {
      orgId: String(org.orgId),
      orgName: org.orgName || '조직',
    })
  })
  ;(Array.isArray(window?.orgFeatureStats) ? window.orgFeatureStats : []).forEach((row) => {
    if (row?.orgId == null) return
    const key = String(row.orgId)
    if (map.has(key)) return
    map.set(key, {
      orgId: key,
      orgName: row.orgName || '조직',
    })
  })
  return Array.from(map.values())
}

function buildCategoryByOrgRows(window, selectedFeatureKey) {
  const rows = Array.isArray(window?.orgFeatureStats) ? window.orgFeatureStats : []
  return mergeChartRows(
    rows.filter((row) => statsFeatureKey(row) === selectedFeatureKey),
    (row) => String(row.orgId ?? row.orgName ?? ''),
    (row) => row.orgName || '조직',
  )
}

function buildOrgByCategoryRows(window, selectedStatsOrgId) {
  const rows = Array.isArray(window?.orgFeatureStats) ? window.orgFeatureStats : []
  return mergeChartRows(
    rows.filter((row) => String(row.orgId) === String(selectedStatsOrgId)),
    (row) => statsFeatureKey(row),
    (row) => row.featureName || '기타',
  )
}

function mergeChartRows(rows, keyOf, nameOf) {
  const map = new Map()
  ;(Array.isArray(rows) ? rows : []).forEach((row) => {
    const key = keyOf(row)
    if (!key) return
    const current = map.get(key) || {
      key,
      name: nameOf(row),
      value: 0,
      usedUnits: 0,
      activeUserCount: 0,
      quotaUnit: row.quotaUnit,
    }
    current.value += numberOrZero(row.eventCount)
    current.usedUnits += numberOrZero(row.usedUnits)
    current.activeUserCount += numberOrZero(row.activeUserCount)
    if (!current.quotaUnit && row.quotaUnit) current.quotaUnit = row.quotaUnit
    map.set(key, current)
  })
  return Array.from(map.values())
    .filter((row) => row.value > 0)
    .sort((a, b) => b.value - a.value || b.usedUnits - a.usedUnits || String(a.name).localeCompare(String(b.name), 'ko-KR'))
    .slice(0, 12)
}

function statsFeatureKey(row) {
  return String(row?.featureCode || row?.featureName || '').trim()
}

function truncateChartLabel(value) {
  const text = String(value || '')
  return text.length > 14 ? `${text.slice(0, 13)}...` : text
}

function formatCompactNumber(value) {
  const number = Number(value || 0)
  if (!Number.isFinite(number)) return '0'
  if (Math.abs(number) >= 10000) {
    return `${(number / 10000).toLocaleString('ko-KR', { maximumFractionDigits: 1 })}만`
  }
  return number.toLocaleString('ko-KR')
}

function SummaryCard({ label, value, subValue = null }) {
  return (
    <div className="rounded border bg-white p-4">
      <div className="text-sm text-slate-500">{label}</div>
      <div className="mt-2 text-xl font-semibold">{value}</div>
      {subValue && <div className="mt-1 text-xs text-slate-500">{subValue}</div>}
    </div>
  )
}

function MiniStat({ label, value }) {
  return (
    <div>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1 font-semibold text-slate-900">{value}</div>
    </div>
  )
}

function isActiveOrgPaysContractValue(contract) {
  const billingMode = contract?.billingMode || contract?.billingModel
  return Boolean(contract?.currentlyEffective) && String(billingMode || '').toUpperCase() === 'ORG_PAYS'
}

function collectUsageCategories(members) {
  const map = new Map()
  ;(members || []).forEach((member) => {
    ;(Array.isArray(member?.usageSummary) ? member.usageSummary : []).forEach((item) => {
      const key = usageItemKey(item)
      if (!key || map.has(key)) return
      map.set(key, {
        key,
        label: item.featureName || '기타',
        quotaUnit: item.quotaUnit,
      })
    })
  })
  return Array.from(map.values())
}

function usageItemKey(item) {
  return String(item?.featureCode || item?.featureName || '').trim()
}

function findUsageItem(items, category) {
  const rows = Array.isArray(items) ? items : []
  return rows.find((item) => usageItemKey(item) === category.key) || null
}

function formatUsageCell(item) {
  if (!item) return '-'
  const unit = quotaUnitLabel(item.quotaUnit)
  const used = numberOrZero(item.usedValue ?? item.used)
  const quotaMode = String(item.quotaMode || '').toUpperCase()
  if (quotaMode === 'UNLIMITED') {
    return `${formatNumber(used)}${unit} / 무제한`
  }
  const remaining = numberOrNull(item.remainingValue ?? item.remaining)
  return `${formatNumber(used)}${unit} / ${remaining == null ? '-' : `${formatNumber(remaining)}${unit}`}`
}

function formatMemberUsageTotal(member) {
  const used = numberOrZero(member?.totalUsedValue)
  const remaining = numberOrNull(member?.totalRemainingValue)
  return `${formatNumber(used)} / ${remaining == null ? '-' : formatNumber(remaining)}`
}

function groupUsageTotals(members) {
  let used = 0
  let remaining = 0
  let hasRemaining = false
  ;(Array.isArray(members) ? members : []).forEach((member) => {
    used += numberOrZero(member?.totalUsedValue)
    const memberRemaining = numberOrNull(member?.totalRemainingValue)
    if (memberRemaining != null) {
      remaining += memberRemaining
      hasRemaining = true
    }
  })
  return { used, remaining: hasRemaining ? remaining : null }
}

function formatFeatureUsage(row) {
  const unit = quotaUnitLabel(row?.quotaUnit)
  return `${formatNumber(row?.usedUnits ?? 0)}${unit}`
}

function quotaUnitLabel(quotaUnit) {
  switch (quotaUnit) {
    case 'CASE':
    case 'COUNT':
      return '건'
    case 'RECIPIENT':
      return '명'
    case 'RUN':
      return '회'
    default:
      return ''
  }
}

function numberOrZero(value) {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

function numberOrNull(value) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function formatNumber(value) {
  const number = typeof value === 'number' && Number.isFinite(value) ? value : Number(value || 0)
  return number.toLocaleString('ko-KR')
}
